import { CheckCircle, Close, HelpOutline } from "@mui/icons-material";
import {
  Alert,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React from "react";
import { ImportItem } from "../../types";

type Props = {
  item: ImportItem | null;
  onClose: () => void;
};

const displayValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "Não informado";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDate = (value: unknown) => {
  if (!value) return "Não informado";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime())
    ? displayValue(value)
    : date.toLocaleString("pt-BR");
};

const accountLabel = (item: ImportItem) => {
  if (item.accountExists === undefined) return "Ainda não consultado";
  return item.accountExists ? "Paciente já cadastrado" : "Paciente não cadastrado";
};

const resultLabel = (item: ImportItem) => {
  if (item.resultExists === undefined) return "Ainda não consultado";
  return item.resultExists ? "Resultado já sincronizado" : "Resultado não sincronizado";
};

const Info = ({ label, value }: { label: string; value: unknown }) => (
  <div style={{ minWidth: 180, flex: "1 1 220px" }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {displayValue(value)}
    </Typography>
  </div>
);

const ImportDetailsDialog: React.FC<Props> = ({ item, onClose }) => {
  if (!item) return null;

  const account = item.data?.account;
  const testResult = item.data?.testeResult;
  const answers = testResult?.quizAnswer ?? [];
  const uniqueQuestions = new Set(
    answers.map(answer => `${answer.quizId}:${answer.question}`)
  ).size;
  const issues = Array.from(
    new Set([item.message, ...item.errors, ...item.warnings].filter(Boolean))
  ) as string[];

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <div>
            <Typography variant="h6">
              {account?.name ?? "Item sem paciente identificado"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conferência dos dados antes da sincronização
            </Typography>
          </div>
          <IconButton onClick={onClose} aria-label="Fechar detalhes">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Chip
              icon={
                item.accountExists === undefined ? (
                  <HelpOutline />
                ) : item.accountExists ? (
                  <CheckCircle />
                ) : (
                  <Close />
                )
              }
              label={accountLabel(item)}
              color={item.accountExists ? "success" : "default"}
              variant="outlined"
            />
            <Chip
              icon={
                item.resultExists === undefined ? (
                  <HelpOutline />
                ) : item.resultExists ? (
                  <CheckCircle />
                ) : (
                  <Close />
                )
              }
              label={resultLabel(item)}
              color={item.resultExists ? "success" : "default"}
              variant="outlined"
            />
            <Chip label={`${answers.length} resposta(s) no arquivo`} color="info" />
            <Chip label={`${uniqueQuestions} pergunta(s) única(s)`} variant="outlined" />
          </Stack>

          {!!issues.length && (
            <Alert severity={item.errors.length ? "error" : "info"}>
              <Typography variant="subtitle2" gutterBottom>
                Diagnóstico
              </Typography>
              {issues.map(issue => (
                <Typography key={issue} variant="body2">
                  • {issue}
                </Typography>
              ))}
            </Alert>
          )}

          <section>
            <Typography variant="subtitle1" gutterBottom>
              Paciente
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <Info label="Nome" value={account?.name} />
              <Info label="E-mail" value={account?.email} />
              <Info label="CPF" value={account?.cpf} />
              <Info label="Telefone" value={account?.phone} />
              <Info label="Nascimento" value={account?.birthDate} />
              <Info label="Gênero" value={account?.genre} />
              <Info label="ID do usuário" value={account?.userId} />
              <Info
                label="ID da conta no servidor"
                value={item.resolvedAccountId ?? account?.accountId ?? account?.id}
              />
            </Stack>
          </section>

          <Divider />

          <section>
            <Typography variant="subtitle1" gutterBottom>
              Teste e origem
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <Info label="ID do resultado" value={testResult?.testResultId} />
              <Info label="Nome do teste" value={testResult?.quizName} />
              <Info
                label="Quiz ID"
                value={testResult?.quizId ?? answers[0]?.quizId}
              />
              <Info label="Total de respostas" value={answers.length} />
              <Info label="Dispositivo / MAC" value={item.device} />
              <Info label="Pacote de origem" value={item.sourceArchive} />
              <Info label="Arquivo JSON" value={item.sourceFile} />
              <Info label="Localização" value={answers[0]?.location} />
            </Stack>
          </section>

          <Divider />

          <section>
            <Typography variant="subtitle1" gutterBottom>
              Respostas encontradas ({answers.length})
            </Typography>
            {!answers.length ? (
              <Alert severity="error">O arquivo não contém respostas para este teste.</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Pergunta</TableCell>
                      <TableCell>Resposta</TableCell>
                      <TableCell>Resposta correta</TableCell>
                      <TableCell>Disparos</TableCell>
                      <TableCell>Início</TableCell>
                      <TableCell>Fim</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {answers.map((answer, index) => (
                      <TableRow key={`${answer.quizId}:${answer.question}:${index}`} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{displayValue(answer.question)}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          {displayValue(answer.answer)}
                          {!!answer.otherAnswers && (
                            <Typography display="block" variant="caption" color="text.secondary">
                              Outras: {displayValue(answer.otherAnswers)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          {displayValue(answer.rightAnswer)}
                        </TableCell>
                        <TableCell>{displayValue(answer.shots)}</TableCell>
                        <TableCell>{formatDate(answer.initDate)}</TableCell>
                        <TableCell>{formatDate(answer.endDateTime)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </section>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDetailsDialog;
